/**
 * The three national flags the language switcher draws, as discs.
 *
 * Inline SVG rather than the flag EMOJI, which is the obvious thing and the wrong
 * one: Windows ships no flag glyphs at all, so 🇦🇹 renders there as the letters
 * "AT" — a switcher whose whole job is to be readable without reading would fall
 * back to a two-letter code on a whole platform. Drawn here, all three are
 * identical on every device and cost no request.
 *
 * NOT drawn in the shared `icons/Icon` frame: that frame is `fill: none` with a
 * `currentColor` stroke, which is exactly right for a pictogram that must take
 * the colour of its label and exactly wrong for the one thing in this product
 * whose colours are fixed by somebody else. Flag colours come from `tokens.css`
 * (the only file that may author a hex) through inline `style`, because a
 * presentation attribute is not a reliable place to resolve `var()`.
 *
 * Round rather than rectangular: at 20px a rectangle has to compete with the
 * pill it sits in, and a disc reads as a mark instead of a picture. The circle is
 * cut by `clip-path` in CSS rather than an SVG `<clipPath>` so the component
 * needs no generated id — three of these render at once, and a shared id would
 * point every disc at the first one's clip. The hairline ring inside the edge is
 * what keeps the white bands of Austria and Hungary off the surface beneath.
 */

const FIELD = { at: 'var(--flag-at-red)', hu: 'var(--flag-hu-red)', gb: 'var(--flag-gb-blue)' };

/**
 * A tricolour of three horizontal bands. Austria's outer bands are its field, so
 * only the middle one is drawn; Hungary's lowest band is its own colour.
 */
function HorizontalBands({ middle, bottom = null }) {
    return (
        <>
            <rect y="8" width="24" height="8" style={{ fill: middle }} />
            {bottom ? <rect y="16" width="24" height="8" style={{ fill: bottom }} /> : null}
        </>
    );
}

/**
 * The Union Flag, drawn in a square rather than its true 2:1 — a disc crops the
 * centre either way, and the square keeps the saltire arms on the diagonal the
 * eye expects. The white saltire is laid down first and the red drawn over it,
 * which at this size is what the counterchanged pale resolves to anyway.
 */
function UnionFlag() {
    return (
        <>
            <path d="M0 0 24 24M24 0 0 24" style={{ stroke: 'var(--flag-white)' }} strokeWidth="7" />
            <path d="M0 0 24 24M24 0 0 24" style={{ stroke: 'var(--flag-gb-red)' }} strokeWidth="2.8" />
            <path d="M12 0v24M0 12h24" style={{ stroke: 'var(--flag-white)' }} strokeWidth="8" />
            <path d="M12 0v24M0 12h24" style={{ stroke: 'var(--flag-gb-red)' }} strokeWidth="4.6" />
        </>
    );
}

const FLAG_ART = {
    at: <HorizontalBands middle="var(--flag-white)" />,
    hu: <HorizontalBands middle="var(--flag-white)" bottom="var(--flag-hu-green)" />,
    gb: <UnionFlag />,
};

/**
 * `aria-hidden`, always: every flag in this product sits beside the language's own
 * name. A flag is a country and never a language — it is the fast visual sort, and
 * the endonym beside it is what actually says what you would be reading.
 */
export default function FlagDisc({ nation, size = 22 }) {
    return (
        <svg
            className="flag-disc"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
        >
            <rect width="24" height="24" style={{ fill: FIELD[nation] }} />
            {FLAG_ART[nation]}
            <circle cx="12" cy="12" r="11.5" fill="none" style={{ stroke: 'var(--ink-100)' }} />
        </svg>
    );
}
